import React from 'react';

export interface SkipLinkProps {
  /**
   * ID of the target element to skip to (without the # prefix).
   * The target element should be the main content area of the page.
   */
  targetId: string;
  /**
   * Custom link text. Should be descriptive of the destination.
   * @default 'Skip to main content'
   */
  children?: React.ReactNode;
}

/**
 * SkipLink component provides a way for keyboard users to skip
 * repetitive navigation and jump directly to the main content.
 *
 * This component meets WCAG 2.4.1 (Bypass Blocks) by providing
 * a mechanism to skip blocks of content that are repeated on
 * multiple pages.
 *
 * The link is visually hidden by default but becomes visible
 * when focused, using the sr-only-focusable pattern.
 *
 * @example
 * // Basic usage in a layout component
 * <SkipLink targetId="main-content" />
 * <header>...</header>
 * <main id="main-content" tabIndex={-1}>
 *   {children}
 * </main>
 *
 * @example
 * // Custom skip link text
 * <SkipLink targetId="search-results">
 *   Skip to search results
 * </SkipLink>
 *
 * @example
 * // Multiple skip links for complex layouts
 * <>
 *   <SkipLink targetId="main-content" />
 *   <SkipLink targetId="sidebar">Skip to sidebar</SkipLink>
 * </>
 */
export const SkipLink: React.FC<SkipLinkProps> = ({
  targetId,
  children = 'Skip to main content',
}) => {
  /**
   * Handles click on the skip link.
   * Sets tabIndex on the target element to make it programmatically focusable,
   * then focuses and scrolls to it.
   */
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.setAttribute('tabindex', '-1');
      target.focus();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      className="
        sr-only
        focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999]
        bg-primary-600 dark:bg-primary-500 text-white
        px-4 py-2 rounded-lg
        focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2
        dark:focus:ring-offset-slate-900
      "
    >
      {children}
    </a>
  );
};

SkipLink.displayName = 'SkipLink';
